import React, { useCallback, useRef, useState } from "react"
import { useFormikContext } from "formik"
import { Button, Grid, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen } from "@fortawesome/pro-regular-svg-icons"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import {
    CreateUpdateProductFormValues,
    ProductStockInfo,
    StockTransactionType,
    StockAdjustmentType,
    StockPopoverFormValues,
    CombinedStockFormValues,
    StockChangeReason,
    ADJUSTMENT_TO_TRANSACTION,
} from "../../productsTypes"
import { StockPopover } from "./StockPopover"

const useStyles = makeStyles()((theme) => ({
    row: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    stockButton: {
        textTransform: "none",
        borderRadius: "0.5rem",
        fontWeight: 500,
        padding: "0.125rem 0.75rem",
        fontSize: theme.typography.body2.fontSize,
        color: "#2196F3",
        borderColor: "#2196F3",
    },
    stockButtonError: {
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
    },
}))

const noop = () => {}

export function StockField() {
    const { classes, cx } = useStyles()
    const { values, setFieldValue, errors } = useFormikContext<CreateUpdateProductFormValues>()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const currentStockRef = useRef<ProductStockInfo>({
        inventory: { total: 0, sold: 0, reserved: 0, available: 0 },
    })
    const initialInventoryRef = useRef<StockPopoverFormValues | undefined>()

    const isCreateMode = !values.stock?.total && !values.stock?.inventory

    const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
        currentStockRef.current = {
            inventory: { total: values.stock?.total ?? 0, sold: 0, reserved: 0, available: 0 },
        }
        if (!initialInventoryRef.current) {
            initialInventoryRef.current = values.stock?.inventory
                ? {
                      type:
                          values.stock.inventory.stockUpdate.transactionType === StockTransactionType.INCREASE
                              ? StockAdjustmentType.INBOUND
                              : StockAdjustmentType.OUTBOUND,
                      quantityDelta: String(values.stock.inventory.stockUpdate.quantityDelta),
                      reason: values.stock.inventory.stockUpdate.reason,
                      note: values.stock.inventory.stockUpdate.note ?? "",
                  }
                : undefined
        }
        setAnchorEl(event.currentTarget)
    }

    const handleClosePopover = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const handleFormCancel = useCallback(() => {
        setFieldValue("stock.inventory", undefined)
        setFieldValue("stock.inventoryIncomplete", false)
        initialInventoryRef.current = undefined
        setAnchorEl(null)
    }, [setFieldValue])

    const handleValuesChange = useCallback(
        (combinedValues: CombinedStockFormValues) => {
            const qty = Number(combinedValues.quantityDelta)
            const hasValidInventory = combinedValues.quantityDelta && !isNaN(qty) && qty > 0 && combinedValues.reason

            const hasPartialInventory =
                (combinedValues.quantityDelta && !isNaN(qty) && qty > 0) || combinedValues.reason

            initialInventoryRef.current = hasPartialInventory
                ? {
                      type: combinedValues.type,
                      quantityDelta: combinedValues.quantityDelta,
                      reason: combinedValues.reason,
                      note: combinedValues.note,
                  }
                : undefined

            setFieldValue("stock.inventoryIncomplete", Boolean(hasPartialInventory && !hasValidInventory))

            setFieldValue(
                "stock.inventory",
                hasValidInventory
                    ? {
                          stockUpdate: {
                              transactionType: ADJUSTMENT_TO_TRANSACTION[combinedValues.type],
                              quantityDelta: qty,
                              reason: combinedValues.reason as StockChangeReason,
                              note: combinedValues.note || undefined,
                          },
                      }
                    : undefined,
            )
        },
        [setFieldValue],
    )

    const baseTotal = values.stock?.total ?? 0
    const pendingUpdate = values.stock?.inventory?.stockUpdate
    const stockTotal = pendingUpdate
        ? pendingUpdate.transactionType === StockTransactionType.DECREASE
            ? baseTotal - pendingUpdate.quantityDelta
            : baseTotal + pendingUpdate.quantityDelta
        : baseTotal
    const hasStockError = typeof errors.stock === "string"

    return (
        <>
            <Grid className={classes.row}>
                <LocalizedStrict id="product-stock-total-stock-label">
                    <Typography variant="body1">Total stock:</Typography>
                </LocalizedStrict>
                <LocalizedStrict id="product-stock-total-stock-items" vars={{ count: stockTotal }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FontAwesomeIcon icon={faPen} />}
                        onClick={handleOpenPopover}
                        className={cx(classes.stockButton, hasStockError && classes.stockButtonError)}
                    >
                        {`${stockTotal} items`}
                    </Button>
                </LocalizedStrict>
            </Grid>

            {anchorEl && (
                <StockPopover
                    anchorEl={anchorEl}
                    onClose={handleClosePopover}
                    productId={0}
                    currentStock={currentStockRef.current}
                    onStockUpdate={noop}
                    formMode
                    disableOutbound={isCreateMode}
                    initialInventoryValues={initialInventoryRef.current}
                    onValuesChange={handleValuesChange}
                    onFormCancel={handleFormCancel}
                    showValidationErrors={hasStockError}
                />
            )}
        </>
    )
}
